import InputWrapper from "../components/InputWrapper";
import ReCaptcha from "../../components/ReCaptcha";
import { Link, useLocation, useParams } from "react-router-dom";
import { useContext, useState, useEffect } from "react";
import { useForm, useWatch } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup';
import Repository from "../../../repository";
import AppContext from "../../../context";
import { Check } from "react-feather";
import Error from "../admin/components/Error";
import PasswordRequirements from "../../components/PasswordRequirements";
import Data from "../../../repository";
import { createAccountSchema } from '../../../validation/schemas';

const CreateAccount = () => {
    const context = useContext(AppContext);
    const { id } = useParams()
    const location = useLocation();
    const isClassCheckout = location.pathname.includes('/classes/');
    const checkoutBasePath = isClassCheckout ? '/classes' : '';

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const [recaptchaToken, setRecaptchaToken] = useState(null);
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [termsError, setTermsError] = useState(false);

    const termsRequired = context.domain?.termsAcceptanceMethod === 'signup' && !!context.domain?.terms;

    const { register, handleSubmit, formState: { errors }, control } = useForm({
        resolver: yupResolver(createAccountSchema),
        defaultValues: {
            email: '',
            password: '',
            firstName: '',
            lastName: '',
            phoneNumber: '',
            subscribe: false
        }
    })

    const email = useWatch({
        name: "email",
        control
    })

    const password = useWatch({
        name: "password",
        control
    })

    const subscribe = useWatch({
        name: "subscribe",
        control
    })

    useEffect(() => {
        if (context.user) {
            if (!id) {
                window.location.href = isClassCheckout ? "/dashboard/settings" : "/account";
                return;
            }
            const search = new URLSearchParams(location.search);
            const hasBundle = search.get('bundle');
            window.location.href = hasBundle ? `${checkoutBasePath}/${id}/checkout/bundle-confirm${location.search}` : `${checkoutBasePath}/${id}/checkout/confirm${location.search}`;
        }
    }, [])

    const submit = async (data) => {
        // Validate reCAPTCHA
        if (process.env.REACT_APP_RECAPTCHA_ENABLED === 'true' && !recaptchaToken) {
            setErrorMessage("Please complete the reCAPTCHA verification.");
            setError(true);
            return;
        }

        if (termsRequired && !termsAccepted) {
            setTermsError(true);
            return;
        }
        setTermsError(false);

        setLoading(true)

        try {
            // Add recaptchaToken to form data
            const accountData = { ...data, recaptchaToken };

            await Repository.createAccount(accountData)
            const result = await Data.login(email, password);
            localStorage.setItem("token", result.data.token);

            if (termsRequired) {
                try {
                    await Data.acceptTerms();
                } catch (termsAcceptError) {
                    console.error("Failed to record terms acceptance", termsAcceptError);
                }
            }

            if (!id) {
                window.location.href = "/account";
                return;
            }
            const search = new URLSearchParams(location.search);
            const hasBundle = search.get('bundle');
            window.location.href = hasBundle ? `${checkoutBasePath}/${id}/checkout/bundle-confirm${location.search}` : `${checkoutBasePath}/${id}/checkout/confirm${location.search}`;

            setLoading(false)
            setError(false)
            setErrorMessage('')
        } catch (error) {
            setErrorMessage(error?.response?.data?.message || '')
            setRecaptchaToken(null);
            setLoading(false)
            setError(true)
        }
    }

    return (
        <div className={"p-8"}>
            <form className={"container mx-auto grid grid-cols-1 gap-4"} onSubmit={handleSubmit(submit)}>
                <div className={"flex items-center gap-4 flex-col md:flex-row"}>
                    <h1>Already have an account?</h1>
                    <Link to={`/login?referer=${window.location.href}`}
                        className={"inline-block bg-primary py-2 px-4 w-full lg:w-auto text-white text-center rounded-md border-0"}>Log In</Link>
                </div>

                <hr className={"my-4"} />

                <h1>Create an Account</h1>

                <div className={"grid grid-cols-1 gap-4 lg:grid-cols-2"}>
                    <InputWrapper label={"Email Address"} input={
                        <input type={"email"} {...register('email')} />
                    } error={errors.email?.message} />

                    <InputWrapper label={"Password"} input={
                        <>
                            <input type={"password"} {...register('password')} />
                            <PasswordRequirements password={password} />
                        </>
                    } error={errors.password?.message} />

                    <InputWrapper label={"First Name"} input={
                        <input type={"text"} {...register('firstName')} />
                    } error={errors.firstName?.message} />

                    <InputWrapper label={"Last Name"} input={
                        <input type={"text"} {...register('lastName')} />
                    } error={errors.lastName?.message} />

                    <InputWrapper label={"Phone Number"} input={
                        <input type={"text"} {...register('phoneNumber')} />
                    } error={errors.phoneNumber?.message} />
                </div>

                <label className={"flex items-center gap-2 cursor-pointer"}>
                    <div className={"relative w-9 h-9"}>
                        <input type={"checkbox"} {...register('subscribe')}
                            className={"w-full h-full checked:bg-primary"} />
                        {subscribe ? <Check color={'#ffffff'} className={"absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"} /> : null}
                    </div>
                    Receive news and updates about {context.domain.name}.
                </label>

                {termsRequired && context.domain.terms ? (
                    <div>
                        <div className={"max-h-40 overflow-y-auto border rounded-md p-3 text-sm text-gray-600"}
                             dangerouslySetInnerHTML={{ __html: context.domain.terms }} />
                        <label className={"flex items-center gap-2 cursor-pointer mt-2"}>
                            <input type={"checkbox"} checked={termsAccepted}
                                   onChange={(event) => {
                                       setTermsAccepted(event.target.checked);
                                       if (event.target.checked) setTermsError(false);
                                   }} />
                            I have read and accept the {context.domain.termsDocumentName || "Terms and Conditions"}
                        </label>
                        {termsError ? <p className={"text-red-500 text-sm"}>You must accept the {context.domain.termsDocumentName || "Terms and Conditions"} to continue.</p> : null}
                    </div>
                ) : null}

                <ReCaptcha
                    onChange={(token) => {
                        setRecaptchaToken(token);
                        if (token) {
                            setError(false);
                            setErrorMessage('');
                        }
                    }}
                    onExpired={() => setRecaptchaToken(null)}
                />

                <div>
                    <button type={'submit'} disabled={loading}
                        className={"inline-block bg-primary py-2 px-4 w-full lg:w-auto text-white text-center rounded-md border-0"}>{loading ? 'Creating Account...' : 'Continue'}</button>
                </div>

                {error ? <Error message={!errorMessage ? "There was an error creating your account, please try again." : errorMessage} /> : null}
            </form>
        </div>
    )
}

export default CreateAccount
